/**
 * Analytics Page
 *
 * SEPARATE reporting dashboard for detailed analysis - the "Microscope" view
 * This is where deeper data analysis happens, distinct from the action-oriented main dashboard
 */

import PageHeader from '@/components/common/PageHeader';
import type { MenuProps } from 'antd';
import { Alert, Button, Dropdown, Tabs } from 'antd';
import React, { useState } from 'react';
import { IoChevronDown, IoDocumentTextOutline, IoDownloadOutline } from 'react-icons/io5';
import { MdDateRange } from 'react-icons/md';
import {
  AdoptionAnalysisTab,
  ConversionAnalysisTab,
  CustomerPerformanceTab,
  ProductPerformanceTab,
  RegionalPerformanceTab
} from '../components';
import { useGetCurrentUser } from '@/hooks/app';
import { PermissionChecker } from '@/utils/permission';
import { REGION_ALL } from '@/utils/constant/appConstant';
import { DateRangeFilterType } from '../types';


// Mock data - this would come from API

// Regional performance data is now fetched directly by the component from the API
// Product performance data is now fetched directly by the component from the API
// Customer adoption data is now fetched directly by the component from the API
// Customer ranking data is now fetched directly by the component from the API

export const AnalyticsPage: React.FC = () => {
  // Get current user permissions
  const { data: currentUserData } = useGetCurrentUser();
  const permissions = currentUserData?.permission;

  // Initialize PermissionChecker
  const permissionChecker = new PermissionChecker(permissions);

  // Permission checks
  const canViewAnalytics = permissionChecker.canViewAnalytics();

  // Region filter for analytics
  const userRegion = currentUserData?.currentUser?.dogma_region;
  const isRegionAll = Boolean(userRegion && userRegion === REGION_ALL);
  const region = !isRegionAll && userRegion ? userRegion : undefined;

  const [activeTab, setActiveTab] = useState('1');
  const [dateRange, setDateRange] = useState<DateRangeFilterType>('lastmonth');

  // Date range menu items
  const dateRangeMenuItems: MenuProps['items'] = [
    {
      key: 'today',
      label: 'Today',
      onClick: () => setDateRange('today'),
    },
    {
      key: 'lastweek',
      label: 'Last Week',
      onClick: () => setDateRange('lastweek'),
    },
    {
      key: 'lastmonth',
      label: 'Last Month',
      onClick: () => setDateRange('lastmonth'),
    },
    {
      key: 'last3months',
      label: 'Last 3 Months',
      onClick: () => setDateRange('last3months'),
    },
    {
      key: 'last6months',
      label: 'Last 6 Months',
      onClick: () => setDateRange('last6months'),
    },
    {
      key: 'lastyear',
      label: 'Last Year',
      onClick: () => setDateRange('lastyear'),
    },
  ];

  // Get current date range label
  const getDateRangeLabel = () => {
    switch (dateRange) {
      case 'today':
        return 'Today';
      case 'lastweek':
        return 'Last Week';
      case 'lastmonth':
        return 'Last Month';
      case 'last3months':
        return 'Last 3 Months';
      case 'last6months':
        return 'Last 6 Months';
      case 'lastyear':
        return 'Last Year';
      default:
        return 'Last Month';
    }
  };

  const handleExportExcel = () => {
  };

  const handleExportPdf = () => {
  };


  const tabItems = [
    {
      key: '1',
      label: 'Adoption Analysis',
      children: <AdoptionAnalysisTab dateRange={dateRange} region={region} />,
    },
    {
      key: '2',
      label: 'Conversion Analysis',
      children: <ConversionAnalysisTab dateRange={dateRange} region={region} />,
    },
    {
      key: '3',
      label: 'Product Performance',
      children: <ProductPerformanceTab region={region} />,
    },
    {
      key: '4',
      label: 'Regional Performance',
      children: <RegionalPerformanceTab dateRange={dateRange} region={region} />,
    },
    {
      key: '5',
      label: 'Customer Performance',
      children: <CustomerPerformanceTab dateRange={dateRange} region={region} />,
    },
    // {
    //   key: '6',
    //   label: 'Time-Based Trends',
    //   children: <TimeBasedTrendsTab />,
    // },
  ];

  return (
    <div className="p-4">
      <PageHeader
        title="Analytics & Reporting"
        subtitle="Deep analysis and reporting - your 'microscope' view into the data"
        breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'Analytics' }]}
        actions={
          canViewAnalytics ? (
            <>
              <Dropdown menu={{ items: dateRangeMenuItems, selectedKeys: [dateRange] }} trigger={['click']}>
                <Button
                  type="default"
                  icon={<MdDateRange size={18} />}
                >
                  {getDateRangeLabel()}
                  <IoChevronDown size={16} className="ml-1" />
                </Button>
              </Dropdown>
              <Button
                icon={<IoDownloadOutline />}
                onClick={handleExportExcel}
              >
                Export Excel
              </Button>
              <Button
                type="primary"
                icon={<IoDocumentTextOutline />}
                onClick={handleExportPdf}
              >
                Generate PDF
              </Button>
            </>
          ) : undefined
        }
      />

      {/* Tabs for different analysis sections */}
      {canViewAnalytics && (
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}

          className='[&_.ant-tabs-tab-btn]:text-base'
        />
      )}
    </div>
  );
};

export default AnalyticsPage;
